import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft } from '@fortawesome/pro-solid-svg-icons/faAngleLeft';
import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { Modal } from 'ui-components/components/modal';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { ModalBody } from 'ui-components/components/modal/modal-body';

import { savePrintSettings } from '../../../../store/print-settings/print-settings.actions';
import ModalPortal from '../../../../components/modal-portal';
import { DocumentType } from '../../../../enums/document-type';
import { Segment } from '../../../../services/analytics-services/segment';
import { PRINTING_SETUP_COMPLETE } from '../../../../constants/analytics-events';
import PrintToOptions from './print-to-options';
import SelectPrinter from './select-printer';

import messages from './../messages';
import styles from './styles.module.less';

const SelectPrinterBody = ({ setPrintSettingForDocType, goBackToOptions }) => (
  <>
    <div className={styles['print-to-description']}>
      <Button className={styles['back-to-options']} onClick={goBackToOptions} theme='link'>
        <FontAwesomeIcon className={styles['back-icon']} icon={faAngleLeft} />
        <FormattedMessage {...messages.backToOptions} />
      </Button>
    </div>
    <div className={styles['modal-body']}>
      <SelectPrinter setPrintSettingForDocType={setPrintSettingForDocType} />
    </div>
  </>
);

SelectPrinterBody.propTypes = {
  setPrintSettingForDocType: PropTypes.func.isRequired,
  goBackToOptions: PropTypes.func.isRequired,
};

const OptionsBody = ({ setConnectOptionSelected, setPrintSettingForDocType }) => (
  <>
    <div className={styles['print-to-description']}>
      <FormattedMessage {...messages.printToModalDescription} />
    </div>
    <div className={styles['modal-body']}>
      <PrintToOptions setConnectOptionSelected={setConnectOptionSelected} setPrintSettingForDocType={setPrintSettingForDocType} />
    </div>
  </>
);

OptionsBody.propTypes = {
  setConnectOptionSelected: PropTypes.func.isRequired,
  setPrintSettingForDocType: PropTypes.func.isRequired,
};

export const PrintToModal = ({ onClose, docType }) => {
  const dispatch = useDispatch();
  const [isConnectOptionSelected, setConnectOptionSelected] = useState(false);

  const setPrintSettingForDocType = useCallback(
    (value) => {
      const { printSettingKey } = docType;
      dispatch(savePrintSettings(printSettingKey, value));
      onClose();
      Segment.trackEvent(PRINTING_SETUP_COMPLETE, {
        printSettingKey,
        value,
      });
    },
    [dispatch, onClose, docType],
  );

  const goBackToOptions = useCallback(() => setConnectOptionSelected(false), [setConnectOptionSelected]);

  return (
    <ModalPortal onClose={onClose}>
      <Modal>
        <ModalHeader>
          <FormattedMessage {...docType.documentNameTranslationForPrintSettingsModal} />
        </ModalHeader>
        <ModalBody>
          {!isConnectOptionSelected ? (
            <OptionsBody setConnectOptionSelected={setConnectOptionSelected} setPrintSettingForDocType={setPrintSettingForDocType} />
          ) : (
            <SelectPrinterBody goBackToOptions={goBackToOptions} setPrintSettingForDocType={setPrintSettingForDocType} />
          )}
        </ModalBody>
      </Modal>
    </ModalPortal>
  );
};

PrintToModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  docType: PropTypes.oneOf(Object.values(DocumentType)).isRequired,
};
